import * as R from 'ramda'
import {
  GetSubscriptions,
  JoinHall,
  JoinHallViaLink,
  LeaveHall,
  MuteSubscription,
  reqGetSubscriptions,
  reqJoinHall,
  reqJoinHallViaLink,
  reqLeaveHall,
  reqMuteSubscription,
  reqSetSubscriptionUnreadMark,
  reqSyncSubscription,
  SetSubscriptionUnreadMark,
  Subscription,
  SyncSubscription,
} from '@weblite/ts-action/subscription'
import { Room } from '@weblite/ts-action/room'
import { Hall } from '@weblite/ts-action/hall'
import { decodeSubscriptionPermission } from '@weblite/permission'
import * as R from 'ramda'
import { request, requestNow } from '$setup/ws'
import {
  showChatRemovedSnackbar,
  showInvalidLinkSnackbar,
  showJoinRequestSentSuccessfullySnackbar,
  showYouHaveAlreadyRequestedToJoinSnackbar,
} from '$comp/snackbars'
import { getIsSuperUser } from '$logic/client-user'
import { setSeenRoomAllMessages } from '$logic/room-seen'
import { updateRoomLastMessageDebounced } from '$logic/room-last-message'
import { updateAdministeredChannelByChannelId } from '$logic/administered-channels'
import { subscriptionsStorage } from './storage'
import * as sync from './offline-sync'
import * as roomService from '../room'
import * as hallService from '../hall' // TODO: There is dependency cycle here via ./hall.service:1=>../subscription:42
import { MemberPermissions } from '../types/subscription'
import { isClientsRoom } from '../room'

export async function fetchSubscriptionsDuringSyncRooms(hallIds: string[]) {
  const subscriptions = await Promise.all(
    hallIds.map(subscriptionsStorage.getFromRedux),
  )
  const subscriptionsToObject = R.fromPairs(
    subscriptions.map((subscription, index) => [
      hallIds[index],
      subscription?.updatedAt || 0,
    ]),
  )
  const res = await requestNow<GetSubscriptions>(
    reqGetSubscriptions({ subscriptions: subscriptionsToObject }),
  )
  if (res.error) return
  return res.payload.subscriptions.forEach(subscription => {
    subscriptionsStorage.set(subscription.hallId, subscription)
  })
}

export const getSubscriptionFromStorage = (roomId: string) => {
  return subscriptionsStorage.get(roomId)
}

export const getSubscription = (roomId: string) => {
  if (isClientsRoom(roomId)) return getSubscriptionFromStorage(roomId)
  return null
}

export async function setSubscriptionMute(
  roomId: string,
  muted: boolean,
): Promise<void> {
  // NOTE: placement is better to be placed here
  const unsetSync = await sync.set(roomId, { muted })
  await subscriptionsStorage.set(roomId, { muted })

  // NOTE: should resolve after changing in frontend for good offline experience. hence request has no await
  request<MuteSubscription>(reqMuteSubscription({ roomId, value: muted })).then(
    async res => {
      await subscriptionsStorage.set(roomId, {
        updatedAt: res.payload.updatedAt,
      })
      await unsetSync()
    },
  )
}

export async function syncSubscription(): Promise<void> {
  const { items, del, release } = await sync.getAll()
  if (!items.length) {
    release()
    return
  }

  try {
    const subscriptions = items.map(item => ({
      hallId: item.id,
      ...item.patch,
    }))

    const res = await requestNow<SyncSubscription>(
      reqSyncSubscription({ subscriptions }),
    )
    await Promise.all(
      res.payload.subscriptions.map(({ hallId, ...fields }) =>
        subscriptionsStorage.set(hallId, fields).then(() => {
          if (fields && 'firstVisibleIid' in fields)
            updateRoomLastMessageDebounced(hallId)
        }),
      ),
    )
    await del()
  } finally {
    release()
  }
}

export async function setSubscriptionUnreadMark(
  roomId: string,
  unreadMark: boolean,
) {
  const subscription = await getSubscription(roomId)
  if (!subscription) return
  if (subscription.unreadMark === unreadMark) return

  const unsetSync = await sync.set(roomId, { unreadMark })
  await subscriptionsStorage.set(roomId, { unreadMark })

  // NOTE: should resolve after changing in frontend for good offline experience. hence request has no await
  request<SetSubscriptionUnreadMark>(
    reqSetSubscriptionUnreadMark({ roomId, value: unreadMark }),
  ).then(async res => {
    await subscriptionsStorage.set(roomId, {
      updatedAt: res.payload.updatedAt,
    })
    await unsetSync()
  })
}

export async function markSubscriptionAsRead(roomId: string): Promise<void> {
  await setSubscriptionUnreadMark(roomId, false)

  await setSeenRoomAllMessages(roomId)
}

export async function leaveHall(roomId: string, disableSnackbar = false) {
  const res = await request<LeaveHall>(reqLeaveHall({ roomId }))
  if (res.error) return false
  await roomService.pruneRoom(roomId)
  if (!disableSnackbar) showChatRemovedSnackbar()
  return true
}

export async function addHallDependencies(payload: {
  room: Room
  hall: Hall
  subscription: Subscription
}) {
  const { room, hall, subscription } = payload
  await Promise.all([
    subscriptionsStorage.add(subscription),
    hallService.addHall(hall),
    roomService.addRoom(room),
    room.type === 'CHANNEL'
      ? updateAdministeredChannelByChannelId(room.id, false)
      : Promise.resolve(),
  ])
}

function joinGAEvent(roomId: string) {
  window.gtag?.('event', 'join_group', { group_id: roomId })
}

export async function joinHall(roomId: string): Promise<boolean> {
  const res = await request<JoinHall>(reqJoinHall({ roomId }))
  if (res.error) return false
  joinGAEvent(roomId)
  await addHallDependencies(res.payload)
  return true
}

export async function joinHallViaLink(
  link: string,
  needsApproval = false,
): Promise<boolean> {
  const res = await request<JoinHallViaLink>(reqJoinHallViaLink({ link }))
  if (res.error) {
    // NOTE: res.error.code = 'revoked-link' | 'expired-link' | 'not-found-link' | 'user-has-not-access' | 'already-requested'
    if (needsApproval && res.error.code === 'already-requested') {
      showYouHaveAlreadyRequestedToJoinSnackbar()
    } else {
      showInvalidLinkSnackbar()
    }
    return false
  }

  if (needsApproval) showJoinRequestSentSuccessfullySnackbar()
  else await addHallDependencies(res.payload)

  if (res.payload.room) joinGAEvent(res.payload.room.id)
  return true
}

const NULL_PERMISSIONS: MemberPermissions = {
  addMembers: false,
  addNewAdmins: false,
  banUsers: false,
  changeHallInfo: false,
  deleteMessages: false,
  editMessage: false,
  inviteViaLink: false,
  isAdmin: false,
  isOwner: false,
  pinMessage: false,
  sendMedia: false,
  sendMessage: false,
  sendWapp: false,
}

export const SUPER_USER_PERMISSIONS: MemberPermissions = {
  ...R.map(R.T, NULL_PERMISSIONS),
  isSuperUser: true,
}

export async function decodeSubscriptionPermissions(
  hallId: string,
): Promise<MemberPermissions> {
  const hall = await hallService.getHall(hallId)
  const subscription = await getSubscription(hallId)
  const isSuperUser = getIsSuperUser()
  return decodeMemberPermissions(hall, subscription, isSuperUser)
}

export function decodeMemberPermissions(
  hall: Hall | undefined,
  subscription: Subscription | undefined | null,
  isSuperUser = false,
): MemberPermissions {
  if (isSuperUser) return SUPER_USER_PERMISSIONS
  if (!hall || !subscription || hall.deletedAt) return NULL_PERMISSIONS

  return decodeSubscriptionPermission(
    hall.permissions,
    subscription.permissions,
  )
}
